import cn from 'classnames';
import { type HTMLAttributes, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../icons';
import { MINIMUM_EVENT_HEIGHT } from '../../constants';
import type { CalendarEvent, CalendarMode, EventType, EventWithDimensions, RsvpStatus, Status } from '../../types';
import styles from './event-item.module.css';
import { useCalculateDisplayRows, useGetEventTitleStyle } from './hooks';

type Properties<T extends CalendarEvent> = {
  calendarEvent: CalendarEvent | EventWithDimensions<T>;
  mode?: CalendarMode;
  type?: EventType;
} & HTMLAttributes<HTMLButtonElement>;

const getEventItemClassName = ({
  isTimeSlotEvent,
  mode,
  rsvp,
  status,
}: {
  isTimeSlotEvent: boolean;
  mode?: CalendarMode;
  rsvp: RsvpStatus;
  status: Status;
}) =>
  cn(styles['event-item'], {
    [styles['event-item-accepted'] ?? '']: rsvp === 'accepted',
    [styles['event-item-all-day'] ?? '']: !isTimeSlotEvent,
    [styles['event-item-canceled'] ?? '']: status === 'canceled',
    [styles['event-item-invited'] ?? '']: rsvp === 'invited',
    [styles['event-item-month'] ?? '']: mode === 'month',
  });

const getEventItemTitleClassName = ({ titleRows }: { titleRows: number }) =>
  cn(styles['event-item-title'], {
    [styles['event-item-title-multi-line'] ?? '']: titleRows > 1,
  });

const EventItem = <T extends CalendarEvent>({ calendarEvent, mode, type = 'time-slot', ...rest }: Properties<T>) => {
  const { t } = useTranslation(['translationReactComponents']);
  const getEventTitleStyle = useGetEventTitleStyle();
  const calculateDisplayRows = useCalculateDisplayRows({
    hasSubtitle: Boolean(calendarEvent.subtitle),
    height: 'height' in calendarEvent ? calendarEvent.height : MINIMUM_EVENT_HEIGHT,
  });

  const isTimeSlotEvent = useMemo(() => type === 'time-slot', [type]);
  const shouldShowEventTime = useMemo(
    () => mode === 'month' && !calendarEvent.isAllDay,
    [calendarEvent.isAllDay, mode],
  );

  const { shouldShowSubtitle, shouldShowTime, titleRows } = useMemo(() => {
    if (!isTimeSlotEvent) {
      return { shouldShowSubtitle: false, shouldShowTime: false, titleRows: 1 };
    }
    return calculateDisplayRows;
  }, [isTimeSlotEvent, calculateDisplayRows]);

  return (
    <button
      {...rest}
      className={getEventItemClassName({
        isTimeSlotEvent,
        mode,
        rsvp: calendarEvent.rsvp,
        status: calendarEvent.status,
      })}
      key={calendarEvent.id}
      tabIndex={0}
      title={calendarEvent.title}
      type="button"
    >
      <div
        className={getEventItemTitleClassName({
          titleRows,
        })}
        style={getEventTitleStyle({ titleRows })}
      >
        {shouldShowEventTime ? (
          <span className={styles['event-item-time']}>{t('dateFormats.time', { date: calendarEvent.start })}</span>
        ) : null}
        {calendarEvent.title}
      </div>
      {isTimeSlotEvent && shouldShowSubtitle && calendarEvent.subtitle ? (
        <div className={styles['event-item-info']}>{calendarEvent.subtitle}</div>
      ) : null}
      {isTimeSlotEvent && shouldShowTime ? (
        <div className={styles['event-item-info']}>
          <Icon icon="time-line" />{' '}
          {t('dateFormats.time', {
            date: { end: calendarEvent.end, start: calendarEvent.start },
          })}
        </div>
      ) : null}
      {calendarEvent.icon ? <span className={styles['event-item-icon']}>{calendarEvent.icon}</span> : null}
    </button>
  );
};

export default EventItem;
